import { addMinutes, differenceInMinutes, isBefore } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import { DEFAULT_TIMEZONE, getTzDateInUtc } from '@shared/dates'
import {
  getInvoiceDates,
  getInvoiceItemsForDisplayName,
  getInvoiceRequestDates,
  getInvoiceSubStatus,
  getPrimaryServiceType,
  isCancelState,
  isInvoiceConfirmed,
  isVisitInvoice,
} from '@shared/invoices'
import { i18WithParams as t } from '@shared/locales'
import { CustomInvoiceAdminDto, InvoiceClientDto, OrderClientDto } from 'service-api/src/types/api'

export type RefundSetting = {
  id: string
  before: number
  after: number
  order: number
  rate: string
  status: string
}

/**
 * Resolves refund settings from order.data, supporting both new (paymentPolicies) and legacy (flat refundSettings) formats.
 */
const resolveRefundSettings = (order: OrderClientDto): RefundSetting[] => {
  const data = order.data as Record<string, unknown> | undefined
  if (!data) return []
  const policies = data.paymentPolicies as Record<string, { refundSettings?: RefundSetting[] }> | undefined
  if (policies) {
    const firstKey = Object.keys(policies)[0]
    return policies[firstKey]?.refundSettings || []
  }
  return (data.refundSettings as RefundSetting[]) || []
}

export const canCancelInvoice = (invoice: InvoiceClientDto, order: OrderClientDto, isAdmin: boolean) => {
  const invoiceSubStatus = getInvoiceSubStatus(invoice)

  if (!isInvoiceConfirmed(invoice)) {
    return false
  }
  const now = Date.now()
  if (isAdmin) {
    return isCancelState(invoice)
  } else {
    // To be replaced with invoice.allowedActions.includes('CANCEL')
    // pending BE change: https://goose-pet.atlassian.net/browse/GA-1804
    let baseDate = invoice.period?.[0]?.startDate
    if (invoiceSubStatus.isRequested) {
      baseDate = invoice.requestPeriods?.[0]?.startDate
    }
    const startDateAt12AM = getTzDateInUtc(baseDate, null, invoice.location?.timezone || DEFAULT_TIMEZONE)
    return isBefore(now, startDateAt12AM)
  }
}

export const getRefundRestrictions = (order: OrderClientDto, invoiceStartDate: Date) => {
  let refundSettingToApply: RefundSetting | undefined,
    maxCancelBy = 0
  const today = new Date().getTime(),
    refundSettings = resolveRefundSettings(order)
  refundSettings.forEach((setting) => {
    if (parseFloat(setting.rate)) {
      const cancelBy = addMinutes(invoiceStartDate, -setting.before).getTime()
      maxCancelBy = maxCancelBy ? Math.max(maxCancelBy, cancelBy) : cancelBy
      if (!refundSettingToApply && today < cancelBy) {
        refundSettingToApply = setting
      }
    }
  })
  return { refundSettingToApply, maxCancelBy }
}

export const invoiceServiceName = (invoice: CustomInvoiceAdminDto) => {
  const isVisit = isVisitInvoice(invoice)
  const isGroom = invoice.locationServiceType?.serviceType?.name === 'grooming'
  const item = isVisit ? (invoice.items || []).find((item) => item.offerType === 'PRIMARY') : invoice.items?.[0]

  let offerName = item?.displayName || ''
  if (isVisit) {
    offerName = isGroom
      ? getInvoiceItemsForDisplayName(invoice)
      : item?.offer?.displayName || item?.offer?.locationServiceType?.displayName || ''
  }

  const petNames = (item?.petRelations || [])
    .map(({ invoicePet }) => invoicePet?.displayName || invoicePet?.name || '')
    .filter((name) => !!name)
    .join(', ')
  return `${offerName}${petNames ? ` (${petNames})` : ''}`
}

const invoiceRequestDates = (invoice: CustomInvoiceAdminDto) => {
  const timezone = invoice.location?.timezone || DEFAULT_TIMEZONE
  const dates = getInvoiceRequestDates(invoice)
  const { isPendingScheduled } = getInvoiceSubStatus(invoice)

  const timeFormat = isPendingScheduled ? ', h:mm aaaa' : ''
  return (dates?.map((d) => `${formatInTimeZone(d.startDate, timezone, `EEE, MMM d, yyyy${timeFormat}`)}`) || []).join(
    ', '
  )
}

const DEFAULT_DATE_FORMAT = 'MMM d, yyyy'

const invoiceServiceDates = (invoice: CustomInvoiceAdminDto) => {
  const { isCanceled, isDeclined } = getInvoiceSubStatus(invoice)
  if (isCanceled || isDeclined) return ''

  const timezone = invoice.location?.timezone || DEFAULT_TIMEZONE

  const { startDate, endDate } = getInvoiceDates(invoice)
  if (getPrimaryServiceType(invoice)?.overnight) {
    return `${formatInTimeZone(startDate, timezone, DEFAULT_DATE_FORMAT)} - ${formatInTimeZone(
      endDate,
      timezone,
      DEFAULT_DATE_FORMAT
    )}`
  }
  // eslint-disable-next-line no-extra-boolean-cast
  const timeFormat = !!invoice.period?.[0]?.startTime ? ', h:mm aaaa' : ''
  return formatInTimeZone(startDate, timezone, `EEE, MMM d, yyyy${timeFormat}`)
}

export const invoiceDates = (invoice: CustomInvoiceAdminDto) => {
  const { isRequested } = getInvoiceSubStatus(invoice)
  return isRequested ? invoiceRequestDates(invoice) : invoiceServiceDates(invoice)
}

export const invoiceCancelDateTime = (invoice: CustomInvoiceAdminDto, order: OrderClientDto) => {
  const timezone = invoice.location?.timezone || DEFAULT_TIMEZONE
  const refundSettings = resolveRefundSettings(order)
  const { startDate: reservationDate } = getInvoiceDates(invoice)
  const minutesToReservation = differenceInMinutes(reservationDate, new Date())

  const numMinutesCanCancel = refundSettings.reduce((res, setting) => {
    if (minutesToReservation > setting.before) {
      return res ? Math.min(res, setting.before) : setting.before
    }
    return res
  }, 0)

  return numMinutesCanCancel
    ? formatInTimeZone(addMinutes(reservationDate, -numMinutesCanCancel), timezone, DEFAULT_DATE_FORMAT)
    : t('global.label.N/A')
}
